import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Grid,
  IconButton,
  Box,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { db } from "../../firebase"; // Adjust import path based on your Firebase setup
import { ref, onValue, remove } from "firebase/database";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencilAlt,
  faSearch,
  faBolt,
  faUsersSlash,
  faCogs,
  faEyeSlash,
  faWifi,
  faBan,
  faTrash,
  faCalendarTimes,
  faBoxOpen,
  faKey,
  faCalendarCheck,
  faIndustry,
  faWindowClose,
} from "@fortawesome/free-solid-svg-icons"; // Add all the relevant icons

import AddCompanyForm from "../Companies/AddCompanyForm";
import Notification from "../Notification";
import useAdminStatus from "../../Hooks/useAdminStatus";

function CompanyApplicationCards({ searchTerm }) {
  const [companies, setCompanies] = useState([]);
  const [companyNames, setCompanyNames] = useState({});
  const [companySettings, setCompanySettings] = useState({}); // Store company settings
  const [editCompany, setEditCompany] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [applicationData, setApplicationData] = useState({}); // State for application data
  const theme = useTheme();
  const { isAdmin } = useAdminStatus();
  const [expandedCards, setExpandedCards] = useState({});

  const handleToggleExpand = (companyCode) => {
    setExpandedCards((prevExpanded) => ({
      ...prevExpanded,
      [companyCode]: !prevExpanded[companyCode],
    }));
  };

  // Map of settings to icons
  const settingsIcons = {
    DisableCortanaAndBingSearch: faSearch,
    DisableFastStartup: faBolt,
    DisablePeopleIconOnTaskbar: faUsersSlash,
    DisableServices: faCogs,
    DisableTrackingServices: faEyeSlash,
    DisableWiFiSenseAndLiveTiles: faWifi,
    PreventBloatwareReinstallation: faBan,
    RemoveBloatware: faTrash,
    RemoveAdditionalScheduledTasks: faCalendarTimes,
    RemoveProvisionedAppxPackages: faBoxOpen,
    RemoveRegistryKeys: faKey,
    RemoveScheduledTasks: faCalendarCheck,
    RemoveManufacturerBloat: faIndustry,
    RemoveWindows11SpecificFeatures: faWindowClose,
  };

  useEffect(() => {
    // Fetch application data from Firebase
    const fetchApplications = () => {
      const appsRef = ref(db, "Applications");
      onValue(appsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setApplicationData(data); // Save to state
        }
      });
    };

    // Fetch company application data
    const fetchCompanyApplications = () => {
      const companyAppsRef = ref(db, "CompanyApplications");
      onValue(companyAppsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const formattedCompanies = Object.keys(data).map((companyCode) => ({
            companyCode,
            applications: data[companyCode],
          }));
          setCompanies(formattedCompanies);
        }
      });
    };

    // Fetch company settings data
    const fetchCompanySettings = () => {
      const settingsRef = ref(db, "CompanySettings");
      onValue(settingsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setCompanySettings(data); // Save to state
        }
      });
    };

    // Fetch company names (Deployment and Archive data)
    const fetchCompanyNames = () => {
      const companyNamesMap = {};
      const addCompanyName = (companyCode, companyName) => {
        if (
          companyCode &&
          companyName &&
          typeof companyCode === "string" &&
          typeof companyName === "string" &&
          companyCode.trim() !== "" &&
          companyName.trim() !== "" &&
          !companyNamesMap[companyCode]
        ) {
          companyNamesMap[companyCode] = companyName;
        }
      };

      const combineDeploymentData = (data) => {
        for (const machineId in data) {
          const deployment = data[machineId];
          const companyCode = deployment?.companyCode?.trim();
          const companyName = deployment?.companyName?.trim();
          if (companyCode && companyName) {
            addCompanyName(companyCode, companyName);
          }
        }
      };

      const deploymentsRef = ref(db, "Deployments");
      onValue(deploymentsRef, (snapshot) => {
        const data = snapshot.val() || {};
        combineDeploymentData(data);
        setCompanyNames((prevNames) => ({ ...prevNames, ...companyNamesMap }));
      });

      const archiveRef = ref(db, "Archive");
      onValue(archiveRef, (snapshot) => {
        const data = snapshot.val() || {};
        combineDeploymentData(data);
        setCompanyNames((prevNames) => ({ ...prevNames, ...companyNamesMap }));
      });
    };

    // Fetch all necessary data
    fetchApplications();
    fetchCompanyApplications();
    fetchCompanySettings(); // Fetch settings
    fetchCompanyNames();
  }, []);

  const handleEditClick = (companyCode) => {
    setEditCompany(companyCode);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditCompany(null);
  };

  const handleCompanyAdded = () => {
    setOpenDialog(false);
    setEditCompany(null);
  };

  const handleDeleteClick = async (companyCode) => {
    const result = await Notification.confirmDeleteCompany(theme);
    if (result.isConfirmed) {
      try {
        await remove(ref(db, `CompanyApplications/${companyCode}`));
        console.log(`Company ${companyCode} deleted successfully.`);
      } catch (error) {
        console.error(`Failed to delete company ${companyCode}:`, error);
      }
    }
  };

  // Filter companies based on search term
  const filteredCompanies = companies.filter((company) =>
    company.companyCode.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Helper function to render enabled settings as chips or inline text
  const renderSettings = (companyCode) => {
    const settings = companySettings[companyCode];
    // Guard clause to check if settings exist
    if (!settings) return null;

    return Object.keys(settings)
      .filter((settingKey) => settings[settingKey]) // Only show enabled settings
      .map((settingKey) => (
        <Tooltip key={settingKey} title={settingKey} arrow>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              margin: "0 8px",
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: theme.palette.info.main,
                color: theme.palette.primary.main,
                border: `2px solid ${theme.palette.primary.main}`,
                marginBottom: 1,
              }}
            >
              <FontAwesomeIcon icon={settingsIcons[settingKey]} />
            </Avatar>
          </Box>
        </Tooltip>
      ));
  };

  return (
    <>
  <Grid container spacing={3} sx={{ padding: 2 }}>
      {filteredCompanies.map((company, index) => {
        const installedApps = Object.entries(company.applications).filter(
          ([, value]) => value,
        );
        const companyName = companyNames[company.companyCode] || 'Unknown Company';
        const isExpanded = expandedCards[company.companyCode];

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={company.companyCode}>
            <Card
              sx={{
                backgroundColor: theme.palette.success.main,
                color: theme.palette.success.text,
              }}
              onClick={() => handleToggleExpand(company.companyCode)} // Toggle on click
            >
              <CardHeader
                avatar={
                  <Avatar
                    sx={{
                      fontSize: '0.8em',
                      color: theme.palette.success.text,
                      bgcolor: theme.palette.primary.main,
                    }}
                  >
                    {company.companyCode}
                  </Avatar>
                }
                action={
                  <>
                    <IconButton
                      onClick={() => handleEditClick(company.companyCode)}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </IconButton>
                    {isAdmin && (
                      <IconButton
                        onClick={() => handleDeleteClick(company.companyCode)}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() => handleToggleExpand(company.companyCode)}
                      sx={{ color: theme.palette.primary.main }}
                    >
                    </IconButton>
                  </>
                }
                title={companyName}
                subheader={`${installedApps.length} Applications Selected`}
                subheaderTypographyProps={{
                  color: theme.palette.success.text,
                }}
              />
              {isExpanded && (
                <CardContent>
                  {/* Application Icons */}
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ marginBottom: '4px' }}
                  >
                    Applications:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      overflowX: 'hidden',
                      '&:hover': {
                        overflowX: 'scroll',
                      },
                      '&::-webkit-scrollbar': {
                        height: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '4px',
                      },
                    }}
                  >
                    {installedApps.map(([appKey]) => (
                      <Tooltip
                        key={appKey}
                        arrow
                        title={applicationData[appKey]?.name || 'Unknown'}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            margin: '0 8px',
                          }}
                        >
                          <Avatar
                            src={
                              applicationData[appKey]?.icon?.startsWith('http')
                                ? applicationData[appKey].icon
                                : `/icons/${applicationData[appKey]?.icon || 'default.png'}`
                            }
                            alt={applicationData[appKey]?.name}
                            sx={{
                              width: 36,
                              height: 36,
                              backgroundColor: theme.palette.info.main,
                              color: theme.palette.success.text,
                              border: `2px solid ${theme.palette.info.main}`,
                              marginBottom: 1,
                            }}
                          />
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>

                  {/* Render Settings */}
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ marginTop: '12px', marginBottom: '4px' }}
                  >
                    Settings:
                  </Typography>
                  <Box
                    className="scrollable-container"
                    sx={{
                      display: 'flex',
                      overflowX: 'hidden',
                      '&:hover': {
                        overflowX: 'scroll',
                      },
                      '&::-webkit-scrollbar': {
                        height: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '4px',
                      },
                    }}
                  >
                    {renderSettings(company.companyCode)}
                  </Box>
                </CardContent>
              )}
            </Card>
          </Grid>
        );
      })}
    </Grid>

      {/* Edit Company Dialog */}
      {openDialog && (
        <AddCompanyForm
          companyCodeProp={editCompany}
          open={openDialog}
          onClose={handleCloseDialog}
          onCompanyAdded={handleCompanyAdded}
        />
      )}
    </>
  );
}

export default CompanyApplicationCards;
